/**
 * Product card photo gallery + colour swatches (TCL-50).
 *
 * - Cards rendered with `gallery: true` (snippets/product-listing.liquid) hold a
 *   horizontal scroll-snap track of the product's photos. Touch screens swipe it
 *   natively; desktop gets arrows. Photos after the first sit in data-src and
 *   load when the card nears the screen, so collection pages stay light.
 *   Snapping (the .is-snapping class) goes on once the page has loaded or the
 *   card is touched: Safari jumps a snapping track to a random photo while the
 *   page is still loading.
 * - Tapping a colour swatch (snippets/show-colors.liquid) shows that colour's
 *   photo in the card and points the card's links at that variant. Works on
 *   every card, with or without the swipe gallery.
 * - A product video in the photo row (data-tcl-card-video) plays muted, on a
 *   loop, while its slide is on screen (TCL-84). With a mouse, resting on a
 *   card moves its row to the video, and leaving moves it back to the first photo.
 * - Safari stops any card photo still downloading when a shopper opens a
 *   product, and shows it as a "?" box after Back. Those photos are asked for
 *   again when the page comes back (TCL-84).
 *
 * Everything is delegated from document, so cards added later (filters,
 * infinite scroll, recommendations, quick view) work without re-initialising.
 */
(function () {
  'use strict';

  if (window.tclCardGallery) return;
  window.tclCardGallery = true;

  var CARD = '[data-tcl-card-gallery]';
  var TRACK = '[data-tcl-card-track]';
  var SWATCH = '.col-swatch li.color';
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function hydrate(img) {
    if (!img || !img.hasAttribute('data-src')) return;
    var srcset = img.getAttribute('data-srcset');
    if (srcset) img.setAttribute('srcset', srcset);
    img.setAttribute('src', img.getAttribute('data-src'));
    img.removeAttribute('data-src');
    img.removeAttribute('data-srcset');
  }

  // all = false loads just the next photo, so the first swipe is instant.
  function hydrateCard(card, all) {
    var pending = card.querySelectorAll('img[data-src]');
    for (var i = 0; i < pending.length; i++) {
      if (all || i === 0) hydrate(pending[i]);
    }
  }

  var pageLoaded = document.readyState === 'complete';
  var waitingToSnap = [];

  function arm(card) {
    var track = card.querySelector(TRACK);
    if (track) track.classList.add('is-snapping');
  }

  function armWhenLoaded(card) {
    if (pageLoaded) arm(card);
    else waitingToSnap.push(card);
  }

  window.addEventListener('load', function () {
    pageLoaded = true;
    window.requestAnimationFrame(function () {
      waitingToSnap.forEach(arm);
      waitingToSnap = [];
    });
  });

  function slideCount(track) {
    return track.children.length;
  }

  function currentIndex(track) {
    if (!track.clientWidth) return 0;
    return Math.round(track.scrollLeft / track.clientWidth);
  }

  function refresh(track) {
    var card = track.closest(CARD);
    if (!card) return;
    var count = slideCount(track);
    var position = track.clientWidth ? track.scrollLeft / track.clientWidth : 0;
    var index = Math.round(position);
    card.style.setProperty('--tcl-count', count);
    card.style.setProperty('--tcl-pos', Math.max(0, Math.min(count - 1, position)).toFixed(3));
    var prev = card.querySelector('[data-tcl-card-prev]');
    var next = card.querySelector('[data-tcl-card-next]');
    if (prev) prev.disabled = index <= 0;
    if (next) next.disabled = index >= count - 1;
  }

  function goTo(track, index) {
    var slide = track.children[Math.max(0, Math.min(slideCount(track) - 1, index))];
    if (!slide) return;
    hydrate(slide.querySelector('img'));
    track.scrollTo({ left: slide.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  // The wrapper that holds one card's image block and its details block.
  function cardRoot(el) {
    var block = el.closest('.product--details, .product--grid-image');
    return block ? block.parentElement : null;
  }

  function setActiveSwatch(swatch) {
    var list = swatch.parentElement;
    var siblings = list ? list.querySelectorAll('li.color') : [];
    for (var i = 0; i < siblings.length; i++) {
      siblings[i].classList.toggle('is-active', siblings[i] === swatch);
    }
  }

  function pointLinksAt(root, url) {
    if (!url) return;
    var links = root.querySelectorAll('.product--grid-image > a[href], a.js-product--details-link');
    for (var i = 0; i < links.length; i++) links[i].setAttribute('href', url);
  }

  function appendSlide(track, mediaId, src, alt) {
    var slide = document.createElement('div');
    slide.className = 'tcl-card-gallery__slide';
    if (mediaId) slide.setAttribute('data-media-id', mediaId);
    var ratio = document.createElement('div');
    ratio.className = 'box-ratio';
    ratio.style.paddingBottom = '100%';
    var img = document.createElement('img');
    img.setAttribute('src', src);
    img.setAttribute('alt', alt || '');
    img.setAttribute('draggable', 'false');
    ratio.appendChild(img);
    slide.appendChild(ratio);
    track.appendChild(slide);
    return slide;
  }

  // Returns true when the card now shows the swatch's photo.
  function showSwatchPhoto(root, swatch) {
    var imageWrap = root.querySelector('.product--grid-image');
    if (!imageWrap) return false;
    var mediaId = swatch.getAttribute('data-media-id');
    var src = swatch.getAttribute('data-href');
    var alt = swatch.getAttribute('data-option-title');
    var track = imageWrap.querySelector(TRACK);

    if (track) {
      var slide = mediaId ? track.querySelector('[data-media-id="' + mediaId + '"]') : null;
      if (!slide) {
        if (!src) return false;
        slide = appendSlide(track, mediaId, src, alt);
      }
      goTo(track, Array.prototype.indexOf.call(track.children, slide));
      refresh(track);
      return true;
    }

    var img = imageWrap.querySelector('.reveal > .box-ratio img');
    if (!img || !src) return false;
    img.removeAttribute('srcset');
    img.setAttribute('src', src);
    imageWrap.classList.add('tcl-swatch-picked');
    return true;
  }

  // After a swipe, light up the swatch whose photo is now showing.
  function syncSwatchToSlide(track) {
    var slide = track.children[currentIndex(track)];
    var root = cardRoot(track);
    if (!slide || !root) return;
    var mediaId = slide.getAttribute('data-media-id');
    if (!mediaId) return;
    var swatch = root.querySelector(SWATCH + '[data-media-id="' + mediaId + '"]');
    if (!swatch) return;
    setActiveSwatch(swatch);
    pointLinksAt(root, swatch.getAttribute('data-variant-url'));
  }

  // ---- Events ----

  var settleTimers = new WeakMap();

  document.addEventListener('scroll', function (event) {
    var track = event.target;
    if (!track || !track.matches || !track.matches(TRACK)) return;
    hydrateCard(track.closest(CARD) || track, true);
    refresh(track);
    window.clearTimeout(settleTimers.get(track));
    settleTimers.set(track, window.setTimeout(function () { syncSwatchToSlide(track); }, 140));
  }, { capture: true, passive: true });

  document.addEventListener('click', function (event) {
    var arrow = event.target.closest('[data-tcl-card-prev], [data-tcl-card-next]');
    if (arrow) {
      var card = arrow.closest(CARD);
      var track = card && card.querySelector(TRACK);
      if (!track) return;
      event.preventDefault();
      event.stopPropagation();
      card.tclHoverVideo = false;
      hydrateCard(card, true);
      goTo(track, currentIndex(track) + (arrow.hasAttribute('data-tcl-card-next') ? 1 : -1));
      return;
    }

    var link = event.target.closest(SWATCH + ' a');
    if (!link) return;
    // Let people open a colour in a new tab or window as usual.
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    var swatch = link.closest('li.color');
    var root = cardRoot(swatch);
    if (!root || !showSwatchPhoto(root, swatch)) return;
    var picked = root.querySelector(CARD);
    if (picked) picked.tclHoverVideo = false;
    event.preventDefault();
    setActiveSwatch(swatch);
    pointLinksAt(root, swatch.getAttribute('data-variant-url'));
  });

  // First hover or keyboard focus on a card: load the rest of its photos, ready for
  // the arrows. A touch only arms snapping: a tap usually opens the product, which
  // would cut those downloads off half way (TCL-84). A swipe loads them as it starts.
  ['pointerover', 'touchstart', 'focusin'].forEach(function (type) {
    document.addEventListener(type, function (event) {
      var card = event.target && event.target.closest ? event.target.closest(CARD) : null;
      if (!card) return;
      if (type === 'focusin' || (type === 'pointerover' && event.pointerType === 'mouse')) hydrateCard(card, true);
      arm(card);
    }, { capture: true, passive: true });
  });

  // ---- Photos cut off by opening a product (TCL-84) ----
  // Safari keeps the page as it was left, so after Back a photo that was still
  // downloading stays a "?" box. Ask for it again. A photo that fails on a weak
  // signal gets one more try as well.

  var PHOTO = '.product--grid-image > a img';
  var RETRY = /([?&])retry=\d+/;

  function isBroken(img) {
    return img.complete && !img.naturalWidth && !!img.getAttribute('src');
  }

  // After Back, Safari hands the broken copy back for the same address, so each
  // try asks for a slightly different one (retry=1, retry=2). Shopify's CDN
  // serves the same photo for it.
  function marked(url, n) {
    if (RETRY.test(url)) return url.replace(RETRY, '$1retry=' + n);
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'retry=' + n;
  }

  function reload(img) {
    var n = (img.tclRetries || 0) + 1;
    img.tclRetries = n;
    var srcset = img.getAttribute('srcset');
    if (srcset) {
      img.setAttribute('srcset', srcset.replace(/(\S+)(\s+\d+[wx])/g, function (match, url, size) {
        return marked(url, n) + size;
      }));
    }
    img.setAttribute('src', marked(img.getAttribute('src'), n));
  }

  function repairPhotos() {
    var photos = document.querySelectorAll(PHOTO);
    for (var i = 0; i < photos.length; i++) {
      if (isBroken(photos[i])) reload(photos[i]);
    }
  }

  document.addEventListener('error', function (event) {
    var img = event.target;
    if (!img || img.tagName !== 'IMG' || img.tclRetryQueued || !img.matches(PHOTO)) return;
    img.tclRetryQueued = true;
    window.setTimeout(function () { reload(img); }, 1500);
  }, true);

  // ---- Product videos (TCL-84) ----
  // The still stays underneath and the video shows once it is playing, so where
  // autoplay is not allowed (Low Power Mode on iPhone) the card keeps its still.
  // Skipped for shoppers who ask for reduced motion or for less data.

  var VIDEO_SLIDE = '[data-tcl-card-video]';
  var saveData = !!(navigator.connection && navigator.connection.saveData);

  function makeVideo(slide) {
    var box = slide.querySelector('.box-ratio');
    if (!box) return null;
    hydrate(box.querySelector('img'));
    var video = document.createElement('video');
    video.className = 'tcl-card-gallery__video';
    video.muted = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('loop', '');
    video.setAttribute('preload', 'auto');
    video.setAttribute('aria-hidden', 'true');
    video.addEventListener('playing', function () { slide.classList.add('is-playing'); });
    video.src = slide.getAttribute('data-tcl-card-video');
    box.appendChild(video);
    return video;
  }

  function playVideo(slide) {
    var video = slide.querySelector('video');
    // A video cut off by leaving the page will not start again on iPhone: put in a new one.
    if (video && video.error) {
      video.parentNode.removeChild(video);
      slide.classList.remove('is-playing');
      video = null;
    }
    if (!video) video = makeVideo(slide);
    if (!video) return;
    var playing = video.play();
    if (playing && playing.catch) playing.catch(function () {});
  }

  function pauseVideo(slide) {
    var video = slide.querySelector('video');
    if (video && !video.paused) video.pause();
  }

  // On screen means at least 60% of the slide shows, inside the photo row and the window.
  var videoWatcher = 'IntersectionObserver' in window && !reduceMotion && !saveData
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.intersectionRatio >= 0.6) playVideo(entry.target);
          else pauseVideo(entry.target);
        });
      }, { threshold: [0, 0.6] })
    : null;

  function watchVideos(scope) {
    if (!videoWatcher) return;
    var slides = scope.querySelectorAll(VIDEO_SLIDE);
    for (var i = 0; i < slides.length; i++) {
      videoWatcher.unobserve(slides[i]);
      videoWatcher.observe(slides[i]);
    }
  }

  // With a mouse nobody swipes, and the video is usually the 3rd photo, so it went
  // unseen on desktop. Resting on a card for a moment moves its row to the video,
  // which then plays as above; leaving moves it back to the first photo. Once the
  // shopper uses the arrows or a swatch, the row stays where they put it.
  var HOVER_DELAY = 350;

  function hoverIn(card) {
    if (card.tclHoverTimer || card.tclHoverVideo) return;
    card.tclHoverTimer = window.setTimeout(function () {
      card.tclHoverTimer = null;
      var track = card.querySelector(TRACK);
      var slide = track && track.querySelector(VIDEO_SLIDE);
      if (!slide || currentIndex(track) !== 0) return;
      card.tclHoverVideo = true;
      goTo(track, Array.prototype.indexOf.call(track.children, slide));
    }, HOVER_DELAY);
  }

  function hoverOut(card) {
    window.clearTimeout(card.tclHoverTimer);
    card.tclHoverTimer = null;
    if (!card.tclHoverVideo) return;
    card.tclHoverVideo = false;
    var track = card.querySelector(TRACK);
    if (track) goTo(track, 0);
  }

  if (videoWatcher) {
    document.addEventListener('pointerover', function (event) {
      if (event.pointerType !== 'mouse') return;
      var card = event.target.closest ? event.target.closest(CARD) : null;
      if (card && card.querySelector(VIDEO_SLIDE)) hoverIn(card);
    }, { passive: true });

    document.addEventListener('pointerout', function (event) {
      if (event.pointerType !== 'mouse') return;
      var card = event.target.closest ? event.target.closest(CARD) : null;
      if (!card || (event.relatedTarget && card.contains(event.relatedTarget))) return;
      hoverOut(card);
    }, { passive: true });
  }

  // Back from a product: mend the cut-off photos, and look again at which videos
  // are on screen, since Safari paused them when the page was left.
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    repairPhotos();
    watchVideos(document);
  });

  // ---- Cards entering the page ----

  var nearScreen = 'IntersectionObserver' in window
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          nearScreen.unobserve(entry.target);
          hydrateCard(entry.target, false);
          armWhenLoaded(entry.target);
        });
      }, { rootMargin: '300px 0px' })
    : null;

  function register(card) {
    if (card.tclCardReady) return;
    card.tclCardReady = true;
    var track = card.querySelector(TRACK);
    if (track) refresh(track);
    watchVideos(card);
    if (nearScreen) {
      nearScreen.observe(card);
    } else {
      hydrateCard(card, true);
      armWhenLoaded(card);
    }
  }

  function scan(scope) {
    if (scope.matches && scope.matches(CARD)) register(scope);
    var cards = scope.querySelectorAll ? scope.querySelectorAll(CARD) : [];
    for (var i = 0; i < cards.length; i++) register(cards[i]);
  }

  function start() {
    scan(document);
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (added[j].nodeType === 1) scan(added[j]);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
